import { useState, useEffect } from 'react';
import axiosInstance from '../api';
import API_ENDPOINTS from '../api/endpoints';
import { parseApiResponse, extractData, isSuccessResponse } from '../api/responseModel';
import { isPartnerFlowActive } from '../utils/partnerFlowUtils';
import { shouldSuppressUserError } from '../api/errorUtils';

/**
 * Hook to detect user's country using backend API
 * Uses /api/Utilities/detect-country endpoint
 */
const useCountryDetection = () => {
    const [detectedCountry, setDetectedCountry] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isPartnerFlowActive()) {
            setIsLoading(false);
            return;
        }

        const detectCountry = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const { ip } = await fetch('https://api.ipify.org?format=json').then((r) => r.json());

                const response = await axiosInstance.get(API_ENDPOINTS.UTILITIES.DETECT_COUNTRY, {
                    headers: {
                        'X-Forwarded-For': ip,
                    },
                });
                const responseData = response?.data;

                // Support both: wrapped format { responseCode, data: { country, country_code } } and direct { country, country_code }
                let data = null;
                if (responseData && responseData.country != null && responseData.country_code != null) {
                    // Direct format: { "country": "Pakistan", "country_code": "PK" }
                    data = { country: responseData.country, country_code: responseData.country_code };
                } else if (isSuccessResponse(parseApiResponse(response))) {
                    // Wrapped format: { responseCode: "200", data: { country, country_code } }
                    data = extractData(parseApiResponse(response));
                }

                if (data && data.country && data.country_code) {
                    setDetectedCountry({
                        code: data.country_code,
                        name: data.country,
                    });
                } else {
                    throw new Error('Country data not available');
                }
            } catch (err) {
                console.error('Error detecting country:', err);
                if (!shouldSuppressUserError(err)) {
                    setError(err.message || 'Failed to detect country');
                }
                setDetectedCountry(null);
            } finally {
                setIsLoading(false);
            }
        };

        let idleId;
        let timeoutId;
        const schedule = () => {
            if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
                idleId = window.requestIdleCallback(() => detectCountry(), { timeout: 2500 });
            } else {
                timeoutId = setTimeout(detectCountry, 1500);
            }
        };

        schedule();

        return () => {
            if (idleId != null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(idleId);
            }
            if (timeoutId != null) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    return { detectedCountry, isLoading, error };
};

export default useCountryDetection;

